import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import ProductImage from '../components/common/ProductImage';
import { useIsMobile } from '../hooks/useResponsive';
import toast from 'react-hot-toast';

const V    = '#8B5CF6';
const VD   = '#6D28D9';
const GOLD = '#C9A96E';
const serif = "'Cormorant Garamond', Georgia, serif";
const sans  = "'Montserrat', sans-serif";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct]         = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedImg, setSelectedImg]   = useState(0);
  const [qty, setQty]                 = useState(1);
  const { addItem } = useCart();
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get(`/api/products/${id}`).then(res => {
      setProduct(res.data);
      setSelectedSize(res.data.variants?.[0]?.size);
    });
  }, [id]);

  if (!product) return (
    <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontFamily: serif, fontSize: 20, color: '#CCC' }}>Chargement...</p>
    </div>
  );

  const variant = product.variants.find(v => v.size === selectedSize);

  return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#111', fontFamily: sans }}>
      {/* Breadcrumb */}
      <div style={{ padding: isMobile ? '0.75rem 1rem' : '1rem 2.5rem', borderBottom: '1px solid #F0ECFF', fontSize: 11, color: '#AAA', letterSpacing: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <Link to="/" style={{ color: '#AAA', textDecoration: 'none' }}>Accueil</Link>
        <span style={{ margin: '0 8px' }}>›</span>
        <Link to="/catalogue" style={{ color: '#AAA', textDecoration: 'none' }}>Catalogue</Link>
        <span style={{ margin: '0 8px' }}>›</span>
        <span style={{ color: VD }}>{product.name}</span>
      </div>

      <div style={{
        maxWidth: 1000, margin: '0 auto',
        padding: isMobile ? '1.5rem 1rem' : '3rem 2rem',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '1.5rem' : '4rem',
      }}>
        {/* Galerie */}
        <div>
          <div style={{ border: '1px solid #F0ECFF', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
            <ProductImage images={product.images ? [product.images[selectedImg]] : []} name={product.name} height={isMobile ? 280 : 420} />
          </div>
          {product.images?.length > 1 && (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {product.images.map((img, i) => (
                <div key={i} onClick={() => setSelectedImg(i)}
                  style={{ width: 64, height: 64, flexShrink: 0, border: `2px solid ${selectedImg===i ? GOLD : '#EEE'}`, borderRadius: 4, overflow: 'hidden', cursor: 'pointer' }}>
                  <ProductImage images={[img]} name={product.name} height={64} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Infos */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {product.badge && (
            <span style={{ display: 'inline-block', fontSize: 9, background: GOLD, color: '#fff', padding: '3px 12px', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 14, fontWeight: 600, alignSelf: 'flex-start' }}>
              {product.badge}
            </span>
          )}
          <h1 style={{ fontFamily: serif, fontSize: isMobile ? 28 : 38, fontWeight: 400, marginBottom: 6, lineHeight: 1.2, color: '#1A0533' }}>{product.name}</h1>
          <p style={{ fontSize: 10, letterSpacing: 4, color: '#AAA', textTransform: 'uppercase', marginBottom: 20 }}>{product.famille}</p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: '#F0ECFF' }} />
            <span style={{ color: GOLD, fontSize: 12 }}>✦</span>
            <div style={{ flex: 1, height: 1, background: '#F0ECFF' }} />
          </div>

          <p style={{ color: '#777', lineHeight: 1.9, marginBottom: 24, fontSize: 13, fontWeight: 300 }}>{product.description}</p>

          {(product.notes?.tete?.length > 0 || product.notes?.coeur?.length > 0 || product.notes?.fond?.length > 0) && (
            <div style={{ background: '#FDFCFF', border: '1px solid #F0ECFF', borderRadius: 4, padding: '1.25rem', marginBottom: 24 }}>
              <p style={{ fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 12, fontWeight: 600 }}>Notes olfactives</p>
              {[['Tête', product.notes?.tete], ['Cœur', product.notes?.coeur], ['Fond', product.notes?.fond]].map(([label, notes]) =>
                notes?.length > 0 && (
                  <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 10, color: '#CCC', minWidth: 36, letterSpacing: 1, textTransform: 'uppercase', paddingTop: 2 }}>{label}</span>
                    <span style={{ fontSize: 13, color: '#555', fontWeight: 300 }}>{notes.join(' · ')}</span>
                  </div>
                )
              )}
            </div>
          )}

          {/* Taille */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 9, letterSpacing: 3, color: '#AAA', textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 }}>Contenance</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {product.variants.map(v => (
                <button key={v.size} onClick={() => setSelectedSize(v.size)} style={{
                  background: selectedSize===v.size ? VD : '#fff',
                  color: selectedSize===v.size ? '#fff' : '#666',
                  border: `1px solid ${selectedSize===v.size ? VD : '#DDD'}`,
                  padding: '9px 18px', cursor: 'pointer', fontSize: 13, borderRadius: 2,
                }}>
                  {v.size} ml
                </button>
              ))}
            </div>
          </div>

          {/* Prix & panier */}
          {variant && (
            <div style={{ marginTop: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 0', borderTop: '1px solid #F0ECFF', borderBottom: '1px solid #F0ECFF', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <p style={{ fontSize: 10, letterSpacing: 2, color: '#AAA', textTransform: 'uppercase', marginBottom: 4 }}>Prix</p>
                  <span style={{ fontFamily: serif, fontSize: isMobile ? 28 : 36, color: VD }}>
                    {variant.price?.toLocaleString()} <span style={{ fontSize: 14, color: '#AAA', fontFamily: sans, fontWeight: 300 }}>FCFA</span>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #EEE', borderRadius: 4 }}>
                  <button onClick={() => setQty(q => Math.max(1, q-1))} style={{ background: '#fff', border: 'none', color: '#666', width: 40, height: 40, cursor: 'pointer', fontSize: 20, borderRadius: '4px 0 0 4px' }}>−</button>
                  <span style={{ width: 40, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{qty}</span>
                  <button onClick={() => setQty(q => q+1)} style={{ background: '#fff', border: 'none', color: '#666', width: 40, height: 40, cursor: 'pointer', fontSize: 20, borderRadius: '0 4px 4px 0' }}>+</button>
                </div>
              </div>

              <button onClick={() => { addItem(product, selectedSize, qty); toast.success(`${product.name} ajouté au panier`); }}
                style={{ width: '100%', background: VD, color: '#fff', border: 'none', padding: '16px', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', cursor: 'pointer', fontFamily: sans, fontWeight: 600, borderRadius: 2 }}
                onMouseEnter={e => e.currentTarget.style.background = GOLD}
                onMouseLeave={e => e.currentTarget.style.background = VD}>
                Ajouter au panier
              </button>

              {variant.stock <= 5 && variant.stock > 0 && (
                <p style={{ fontSize: 11, color: '#F59E0B', marginTop: 10, textAlign: 'center' }}>⚠ Plus que {variant.stock} en stock</p>
              )}
              {variant.stock === 0 && (
                <p style={{ fontSize: 11, color: '#EF4444', marginTop: 10, textAlign: 'center' }}>Rupture de stock</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
